import { cn } from '@/lib/cn';

export type StepIcon = 'register' | 'deposit' | 'direction' | 'track';

export interface Step {
  icon: StepIcon;
  title: string;
  body: string;
}

/**
 * The four steps from first click to first position.
 *
 * A row on wide screens with the connectors the reference draws, a plain stack
 * on a phone. The connectors are decoration and are dropped rather than rotated
 * when it stacks: a dotted line running down a column between two centred cards
 * reads as a divider, not as a sequence, and the numbers already carry the
 * order.
 */
export function HowItWorks({
  steps,
  duration,
  accent,
}: {
  steps: readonly Step[];
  duration: string;
  accent: string;
}) {
  return (
    <section aria-labelledby="moon-how" className="moon-guide">
      <div className="moon-section-heading">
        <div>
          <p className="moon-eyebrow">Adım adım Moon</p>
          <h2 id="moon-how">Nasıl çalışır?</h2>
        </div>
        <p className="moon-guide-duration">
          <ClockGlyph />
          Tahmini başlangıç süresi: {duration}
        </p>
      </div>
      <ol className="moon-steps">
        {steps.map((step, index) => (
          <li key={step.title} className="moon-step" data-tilt>
            <div className="moon-step-top">
              <span className="moon-step-icon" style={{ color: accent }}>
                <StepGlyph name={step.icon} />
              </span>
              <span className="moon-step-number">{String(index + 1).padStart(2, '0')}</span>
            </div>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function StepGlyph({ name }: { name: StepIcon }) {
  if (name === 'direction') {
    // The only two-colour glyph: up is green and down is red everywhere else on
    // this page, and the step is about choosing between them.
    return (
      <svg aria-hidden viewBox="0 0 24 24" className="size-8">
        <path d="M8 4l3.2 4H8.9v8H7.1V8H4.8L8 4z" fill="#34d399" />
        <path d="M16 20l-3.2-4h2.3V8h1.8v8h2.3L16 20z" fill="#f87171" />
      </svg>
    );
  }

  const paths: Record<Exclude<StepIcon, 'direction'>, string> = {
    register:
      'M10 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 10c3.2 0 6 1.3 6 3v3H4v-3c0-1.7 2.8-3 6-3zm9-4h2v2h2v2h-2v2h-2v-2h-2v-2h2v-2z',
    deposit:
      'M20 8h-1V7a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h14a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1zM6 6h10a1 1 0 0 1 1 1v1H6a1 1 0 0 1 0-2zm11 8.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
    track: 'M3 3h2v16h16v2H3V3zm4 12 4-5 3 3.5L20 7l1.5 1.3-6.8 8L11 12l-4 5-1-1.2 1-.8z',
  };

  return (
    <svg aria-hidden viewBox="0 0 24 24" className={cn('size-8 fill-current')}>
      <path d={paths[name]} />
    </svg>
  );
}

function ClockGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-3.5 fill-current">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 10.4V6h-2v7.4l5 3 1-1.7-4-2.3z" />
    </svg>
  );
}
